import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader } from 'lucide-react';
import axios from 'axios';
import './ImageUploader.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUploader = ({ 
  currentUrl, 
  onUrlChange, 
  onUploadSuccess,
  getAuthHeader,
  label = "Image",
  accept = "image/*",
  maxSize = 10 * 1024 * 1024 // 10MB default (matches backend limit)
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentUrl || '');
  const fileInputRef = useRef(null);

  // Sync previewUrl with currentUrl when it changes (e.g., when editing different items)
  useEffect(() => {
    setPreviewUrl(currentUrl || '');
  }, [currentUrl]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const uploadFile = useCallback(async (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image (JPG, PNG, GIF, WebP)');
      return;
    }
    
    // Validate file size
    if (file.size > maxSize) {
      setError(`L'image est trop grande. Maximum: ${Math.round(maxSize / 1024 / 1024)}MB`);
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const authHeaders = getAuthHeader();
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/upload-image`,
        formData,
        {
          headers: {
            ...authHeaders.headers,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          },
        }
      );

      const uploadedUrl = response.data.url;
      setPreviewUrl(uploadedUrl);
      onUrlChange(uploadedUrl);
      if (onUploadSuccess) onUploadSuccess(uploadedUrl);
      setError(null);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'upload de l\'image');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [getAuthHeader, onUrlChange, onUploadSuccess, maxSize]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
  }, [uploadFile]);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
  };

  const handleUrlInputChange = (e) => {
    const url = e.target.value;
    setPreviewUrl(url);
    onUrlChange(url);
    setError(null);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const clearImage = () => {
    setPreviewUrl('');
    onUrlChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="image-uploader">
      <label className="image-uploader-label">{label}</label>
      
      {/* Drop Zone */}
      <div
        className={`image-uploader-dropzone ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {isUploading ? (
          <div className="upload-progress">
            <Loader className="spinner" size={32} />
            <span>Upload en cours... {uploadProgress}%</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        ) : previewUrl ? (
          <div className="image-preview-container">
            <img 
              src={previewUrl} 
              alt="Aperçu" 
              className="image-preview"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <button 
              className="clear-image-btn"
              onClick={(e) => {
                e.stopPropagation();
                clearImage();
              }}
              title="Supprimer l'image"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="dropzone-content">
            <Upload size={32} className="upload-icon" />
            <p className="dropzone-text">
              <strong>Glissez-déposez</strong> une image ici
            </p>
            <p className="dropzone-hint">ou cliquez pour sélectionner</p>
            <p className="dropzone-formats">JPG, PNG, GIF, WebP • Max {Math.round(maxSize / 1024 / 1024)}MB</p>
          </div>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="file-input-hidden"
        />
      </div>

      {/* URL Input */}
      <div className="url-input-section">
        <div className="url-input-divider">
          <span>ou entrez une URL</span>
        </div>
        <div className="url-input-wrapper">
          <ImageIcon size={16} className="url-input-icon" />
          <input
            type="text"
            value={previewUrl || ''}
            onChange={handleUrlInputChange}
            placeholder="https://exemple.com/image.jpg"
            className="url-input"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="upload-error">
          <X size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
